import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';

export const UpdateSchedulePage = ({ userRole }) => {
    const [schedules, setSchedules] = useState([]);
    const [teams, setTeams] = useState([]);
    const [venues, setVenues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');

    const [selectedScheduleId, setSelectedScheduleId] = useState('');
    const [selectedSchedule, setSelectedSchedule] = useState(null);

    const [scheduleConflicts, setScheduleConflicts] = useState([]);
    // State for editing tools
    const [weekToEditIndex, setWeekToEditIndex] = useState('');
    const [matchToEditIndex, setMatchToEditIndex] = useState('');

    // States for specific tools
    const [newVenue, setNewVenue] = useState('');
    const [destinationWeekIndex, setDestinationWeekIndex] = useState('');
    const [newEmptyWeekName, setNewEmptyWeekName] = useState('');
    const [insertionPointIndex, setInsertionPointIndex] = useState('');
    const [newEventName, setNewEventName] = useState('');

    useEffect(() => {
        if (userRole !== 'admin') {
            setLoading(false);
            return;
        }
        const fetchData = async () => {
            setLoading(true);
            try {
                const schedulesSnapshot = await getDocs(collection(db, "schedules"));
                setSchedules(schedulesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

                const teamsSnapshot = await getDocs(collection(db, "teams"));
                setTeams(teamsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

                const venuesSnapshot = await getDocs(collection(db, "venues"));
                setVenues(venuesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (error) {
                setMessage("Failed to load data. " + error.message);
            }
            setLoading(false);
        };
        fetchData();
    }, [userRole]);

    const scanForConflicts = useCallback((weeks) => {
        const conflicts = [];
        weeks.forEach((week, index) => {
            const venueUsage = {};
            week.games.forEach(game => {
                if (game.venue) {
                    venueUsage[game.venue] = (venueUsage[game.venue] || 0) + 1;
                }
            });
            Object.entries(venueUsage).forEach(([venueName, count]) => {
                const venue = venues.find(v => v.name === venueName);
                if (venue && count > venue.boards) {
                    conflicts.push({ week: week.week, venue: venueName, count, capacity: venue.boards });
                }
            });
        });
        setScheduleConflicts(conflicts);
    }, [venues]);

    useEffect(() => {
        // This effect runs ONLY when the main schedule is changed.
        const schedule = schedules.find(s => s.id === selectedScheduleId);
        if (schedule) {
            setSelectedSchedule(schedule);
            scanForConflicts(schedule.weeks);
            // Reset all sub-selections.
            setWeekToEditIndex('');
            setMatchToEditIndex('');
            setNewVenue('');
            setDestinationWeekIndex('');
            setInsertionPointIndex('');
            setNewEventName('');
        } else {
            setSelectedSchedule(null);
            setScheduleConflicts([]);
        }
    }, [selectedScheduleId, schedules, scanForConflicts]);

    useEffect(() => {
        // This effect runs ONLY when the week to edit is changed.
        const week = selectedSchedule?.weeks[weekToEditIndex];
        setNewEventName(week?.games[0]?.eventName || '');
    }, [weekToEditIndex, selectedSchedule]);

    const getTeamName = (teamId) => teams.find(t => t.id === teamId)?.name || 'Unknown Team';

    const handleUpdate = async (updatedWeeks) => {
        try {
            const scheduleDocRef = doc(db, "schedules", selectedScheduleId);
            await updateDoc(scheduleDocRef, { weeks: updatedWeeks });
            setMessage("Schedule updated successfully!");
            // Refresh local state to reflect changes
            const updatedSchedule = { ...selectedSchedule, weeks: updatedWeeks };
            setSelectedSchedule(updatedSchedule);
            setSchedules(schedules.map(s => s.id === selectedScheduleId ? updatedSchedule : s));
            scanForConflicts(updatedWeeks); // Re-scan for conflicts after update
        } catch (error) {
            setMessage("Error updating schedule: " + error.message);
        }
    };

    const handleVenueChange = () => {
        if (weekToEditIndex === '' || matchToEditIndex === '' || !newVenue) {
            setMessage("Please select a week, a match, and a new venue.");
            return;
        }
        const venueData = venues.find(v => v.name === newVenue);
        const gamesAtNewVenue = selectedSchedule.weeks[weekToEditIndex].games.filter(g => g.venue === newVenue).length;
        let confirmationMessage = `You are changing the venue to "${newVenue}" which has ${venueData?.boards || 'an unknown number of'} board(s).`;

        if (venueData && gamesAtNewVenue >= venueData.boards) {
            confirmationMessage += `\n\nWARNING: This will overbook the venue for this week. Are you sure you want to proceed?`;
        } else {
            confirmationMessage += `\n\nThis may cause a scheduling conflict. Are you sure you want to proceed?`;
        }

        if (!window.confirm(confirmationMessage)) {
            return;
        }

        const updatedWeeks = JSON.parse(JSON.stringify(selectedSchedule.weeks));
        updatedWeeks[weekToEditIndex].games[matchToEditIndex].venue = newVenue;
        handleUpdate(updatedWeeks);
    };

    const handleRescheduleMatch = () => {
        if (weekToEditIndex === '' || matchToEditIndex === '' || destinationWeekIndex === '') {
            setMessage("Please select a source week, a match, and a destination week.");
            return;
        }
        if (weekToEditIndex === destinationWeekIndex) {
            setMessage("Source and destination weeks cannot be the same.");
            return;
        }
        const updatedWeeks = JSON.parse(JSON.stringify(selectedSchedule.weeks));
        const matchToMove = updatedWeeks[weekToEditIndex].games.splice(matchToEditIndex, 1)[0];
        updatedWeeks[destinationWeekIndex].games.push(matchToMove);
        handleUpdate(updatedWeeks);
    };

    const handleSwapTeams = () => {
        if (weekToEditIndex === '' || matchToEditIndex === '') {
            setMessage("Please select a week and a match to swap.");
            return;
        }
        const updatedWeeks = JSON.parse(JSON.stringify(selectedSchedule.weeks));
        const match = updatedWeeks[weekToEditIndex].games[matchToEditIndex];
        const oldHomeId = match.homeTeamId;
        const newHomeId = match.awayTeamId;
        const newHomeTeam = teams.find(t => t.id === newHomeId);
        const newVenueName = newHomeTeam?.homeLocation || match.venue;
        const venueData = venues.find(v => v.name === newVenueName);

        let confirmationMessage = `This will swap the home/away teams and change the venue to "${newVenueName}", which has ${venueData?.boards || 'an unknown number of'} board(s).`;

        const gamesAtNewVenue = selectedSchedule.weeks[weekToEditIndex].games.filter(g => g.venue === newVenueName).length;
        if (venueData && gamesAtNewVenue >= venueData.boards) {
            confirmationMessage += `\n\nWARNING: This will overbook the new venue. Are you sure you want to proceed?`;
        }

        if (!window.confirm(confirmationMessage)) {
            return;
        }

        match.homeTeamId = match.awayTeamId;
        match.awayTeamId = oldHomeId;
        // Update venue to new home team's location
        match.venue = newVenueName;
        handleUpdate(updatedWeeks);
    };

    const handleEventNameChange = () => {
        if (weekToEditIndex === '' || !newEventName.trim()) {
            setMessage("Please select an event week and provide a new name.");
            return;
        }
        const updatedWeeks = JSON.parse(JSON.stringify(selectedSchedule.weeks));
        const week = updatedWeeks[weekToEditIndex];

        if (week.week === 'Event' || (typeof week.week === 'string' && week.games[0]?.eventName)) {
            week.games[0].eventName = newEventName;
            handleUpdate(updatedWeeks);
        } else {
            setMessage("The selected week is not a valid event week to rename.");
        }
    };

    const handleInsertEmptyWeek = () => {
        if (insertionPointIndex === '') {
            setMessage("Please select where to insert the new week.");
            return;
        }
        const updatedWeeks = JSON.parse(JSON.stringify(selectedSchedule.weeks));
        const insertionIndex = parseInt(insertionPointIndex, 10) + 1;

        // Create a new empty week
        const weekTitle = newEmptyWeekName.trim() || "New Week";
        const newWeek = { week: weekTitle, date: 'TBD', games: [{ eventName: weekTitle }], byes: [] };
        updatedWeeks.splice(insertionIndex, 0, newWeek);

        // Recalculate dates and week numbers for all subsequent weeks
        for (let i = insertionIndex; i < updatedWeeks.length; i++) {
            // Only increment numeric week numbers
            if (!isNaN(parseInt(updatedWeeks[i].week, 10))) {
                updatedWeeks[i].week = parseInt(updatedWeeks[i].week, 10) + 1;
            }
            const prevWeekDate = new Date(updatedWeeks[i - 1].date);
            prevWeekDate.setDate(prevWeekDate.getDate() + 7);
            updatedWeeks[i].date = prevWeekDate.toLocaleDateString();
        }

        handleUpdate(updatedWeeks);
    };

    const handleRemoveWeek = async () => {
        if (weekToEditIndex === '') {
            setMessage("Please select a week to remove.");
            return;
        }

        const updatedWeeks = JSON.parse(JSON.stringify(selectedSchedule.weeks));
        const weekToRemove = updatedWeeks[weekToEditIndex];
        const weekName = weekToRemove.week === 'Event' || typeof weekToRemove.week === 'string' ? (weekToRemove.games[0]?.eventName || weekToRemove.week) : `Week ${weekToRemove.week}`;

        if (!window.confirm(`Are you sure you want to permanently remove "${weekName}"? This will shift all subsequent weeks up by one week. This action cannot be undone.`)) {
            return;
        }

        // Remove the selected week
        updatedWeeks.splice(weekToEditIndex, 1);

        // Recalculate dates and week numbers for all subsequent weeks
        // Start from the index where the week was removed
        for (let i = parseInt(weekToEditIndex, 10); i < updatedWeeks.length; i++) {
            // The new date should be 7 days earlier than it was
            const originalDate = new Date(updatedWeeks[i].date);
            originalDate.setDate(originalDate.getDate() - 7);
            updatedWeeks[i].date = originalDate.toLocaleDateString();

            // Decrement numeric week numbers
            if (!isNaN(parseInt(updatedWeeks[i].week, 10))) {
                updatedWeeks[i].week = parseInt(updatedWeeks[i].week, 10) - 1;
            }
        }
        handleUpdate(updatedWeeks);
    };

    if (userRole !== 'admin') {
        return <p className="text-xl text-danger text-center">Access Denied. Only administrators can view this page.</p>;
    }

    if (loading) {
        return <div className="text-center">Loading schedule data...</div>;
    }

    const weekToEdit = selectedSchedule?.weeks[weekToEditIndex];

    return (
        <div className="space-y-8">
            <h2 className="page-title heading-primary-1 text-4xl font-extrabold text-center">Update Existing Schedule</h2>
            {scheduleConflicts.length > 0 && (
                <div className="bg-danger-soft border-l-4 border-danger p-4 rounded-md" role="alert">
                    <p className="font-bold text-danger">Venue Conflict Detected!</p>
                    <ul className="list-disc list-inside">
                        {scheduleConflicts.map((conflict, index) => (
                            <li key={index} className="text-danger">Week {conflict.week}: Venue "{conflict.venue}" is overbooked ({conflict.count} games / {conflict.capacity} boards).</li>
                        ))}
                    </ul>
                </div>
            )}
            {message && (
                <p className={`text-center ${message.toLowerCase().includes('error') || message.toLowerCase().includes('fail') ? 'text-danger' : 'text-success'}`}>{message}</p>
            )}

            {/* --- Schedule and Week Selection --- */}
            <div className="card p-6 rounded-lg shadow-md space-y-4">
                <h3 className="heading-primary-1 text-2xl font-bold">1. Select Schedule and Week</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <select value={selectedScheduleId} onChange={e => setSelectedScheduleId(e.target.value)} className="p-2 border border-low rounded surface-low">
                        <option value="">Select a Schedule</option>
                        {schedules.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <select value={weekToEditIndex} onChange={e => setWeekToEditIndex(e.target.value)} className="p-2 border border-low rounded surface-low" disabled={!selectedSchedule}>
                        <option value="">Select a Week</option>
                        {selectedSchedule?.weeks.map((w, i) => <option key={i} value={i}>{w.week === 'Event' ? w.games[0].eventName : `Week ${w.week}`}</option>)}
                    </select>
                </div>
                {/* Remove Week Button */}
                <div className="flex items-end">
                    <button onClick={handleRemoveWeek} className="btn btn-danger py-2 px-4 rounded-lg w-full" disabled={!selectedSchedule || weekToEditIndex === ''}>
                        Remove Selected Week
                    </button>
                </div>
            </div>

            {weekToEdit && (
                <div className="card p-6 rounded-lg shadow-md space-y-6">
                    <h3 className="heading-primary-1 text-2xl font-bold">2. Choose Match and Action</h3>
                    <div>
                        <label className="block text-sm font-medium text-muted mb-1">Select Match to Edit:</label>
                        <select value={matchToEditIndex} onChange={e => setMatchToEditIndex(e.target.value)} className="p-2 border border-low rounded w-full surface-low">
                            <option value="">Select a Match</option>
                            {weekToEdit.games.map((g, i) => !g.eventName && <option key={i} value={i}>{getTeamName(g.awayTeamId)} at {getTeamName(g.homeTeamId)}</option>)}
                        </select>
                    </div>

                    {/* --- Event Name Editing Tool --- */}
                    {weekToEdit && (typeof weekToEdit.week === 'string' || weekToEdit.week === 'Event') && weekToEdit.games[0]?.eventName && (
                        <div className="flex flex-wrap items-end gap-2 border-t border-low pt-4">
                            <div className="flex-grow">
                                <label className="block text-sm font-medium text-muted">Edit Event Name:</label>
                                <input type="text" value={newEventName} onChange={e => setNewEventName(e.target.value)} className="p-2 border border-low rounded w-full surface-low" />
                            </div>
                            <button onClick={handleEventNameChange} className="btn btn-warning py-2 px-4 rounded-lg">Rename Event</button>
                        </div>
                    )}

                    {/* --- Editing Tools --- */}
                    <div className="space-y-4 border-t border-low pt-4">
                        {/* Change Venue */}
                        <div className="flex flex-wrap items-end gap-2">
                            <div className="flex-grow">
                                <label className="block text-sm font-medium text-muted">Change Venue:</label>
                                <select value={newVenue} onChange={e => setNewVenue(e.target.value)} className="p-2 border border-low rounded w-full surface-low" disabled={matchToEditIndex === ''}>
                                    <option value="">Select New Venue</option>
                                    {venues.map(v => <option key={v.id} value={v.name}>{v.name}</option>)}
                                </select>
                            </div>
                            <button onClick={handleVenueChange} className="btn btn-info py-2 px-4 rounded-lg" disabled={matchToEditIndex === ''}>Update Venue</button>
                        </div>

                        {/* Reschedule Match */}
                        <div className="flex flex-wrap items-end gap-2">
                            <div className="flex-grow">
                                <label className="block text-sm font-medium text-muted">Reschedule to Week:</label>
                                <select value={destinationWeekIndex} onChange={e => setDestinationWeekIndex(e.target.value)} className="p-2 border border-low rounded w-full surface-low" disabled={matchToEditIndex === ''}>
                                    <option value="">Select Destination Week</option>
                                    {selectedSchedule?.weeks.map((w, i) => i !== parseInt(weekToEditIndex) && <option key={i} value={i}>{w.week === 'Event' ? w.games[0].eventName : `Week ${w.week}`}</option>)}
                                </select>
                            </div>
                            <button onClick={handleRescheduleMatch} className="btn btn-secondary py-2 px-4 rounded-lg" disabled={matchToEditIndex === ''}>Move Match</button>
                        </div>

                        {/* Swap Teams */}
                        <div className="flex items-center justify-between pt-2">
                            <p className="text-sm font-medium text-muted">Swap Home/Away Teams (updates venue automatically):</p>
                            <button onClick={handleSwapTeams} className="btn btn-secondary py-2 px-4 rounded-lg" disabled={matchToEditIndex === ''}>Swap Teams</button>
                        </div>

                        {/* Insert Empty Week */}
                        <div className="flex flex-wrap items-end gap-2 border-t border-low pt-4">
                            <div className="flex-grow" style={{ flexBasis: '300px' }}>
                                <label className="block text-sm font-medium text-muted">Insert Empty Week After:</label>
                                <select value={insertionPointIndex} onChange={e => setInsertionPointIndex(e.target.value)} className="p-2 border border-low rounded w-full surface-low">
                                    <option value="">Select Insertion Point</option>
                                    {selectedSchedule?.weeks.map((w, i) => <option key={i} value={i}>{w.week === 'Event' ? w.games[0].eventName : `Week ${w.week}`}</option>)}
                                </select>
                            </div>
                            <div className="flex-grow">
                                <label className="block text-sm font-medium text-muted">New Week Name (Optional):</label>
                                <input type="text" value={newEmptyWeekName} onChange={e => setNewEmptyWeekName(e.target.value)} placeholder="e.g., BYE WEEK" className="p-2 border border-low rounded w-full surface-low" />
                            </div>
                            <button onClick={handleInsertEmptyWeek} className="btn btn-primary py-2 px-4 rounded-lg">Insert Week</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UpdateSchedulePage;